/**
 * Utility for API calls with retry logic to handle cold starts
 * When the API is sleeping (cold start), this will retry with exponential backoff
 */

const COLD_START_ERRORS = [
  "Failed to fetch",
  "NetworkError",
  "ERR_CONNECTION_REFUSED",
  "ECONNREFUSED",
  "timeout",
  "503",
  "504",
];

const isColdStartError = (error) => {
  const errorStr = String(error?.message || error || "").toLowerCase();
  return COLD_START_ERRORS.some((e) => errorStr.includes(e.toLowerCase()));
};

/**
 * Fetch with retry logic for cold starts
 * @param {string} url - The URL to fetch
 * @param {object} options - Fetch options
 * @param {object} config - Retry configuration
 * @param {number} config.maxRetries - Maximum number of retries (default: 3)
 * @param {number} config.initialDelay - Initial delay in ms (default: 2000)
 * @param {function} config.onRetry - Callback when retrying (receives attempt number and delay)
 * @param {function} config.onWaiting - Callback when waiting for API to wake up
 * @returns {Promise<Response>}
 */
export const fetchWithRetry = async (url, options = {}, config = {}) => {
  const {
    maxRetries = 3,
    initialDelay = 2000,
    onRetry = null,
    onWaiting = null,
  } = config;

  let lastError = null;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      const response = await fetch(url, options);

      // If we get a 503 or 504, the server might be waking up
      if (response.status === 503 || response.status === 504) {
        if (attempt < maxRetries) {
          const delay = initialDelay * Math.pow(2, attempt);
          if (onRetry) {
            onRetry(attempt + 1, delay);
          }
          if (onWaiting) {
            onWaiting(
              true,
              `Server is waking up... Retrying in ${Math.round(delay / 1000)}s`
            );
          }
          await new Promise((resolve) => setTimeout(resolve, delay));
          continue;
        }
      }

      // Success or non-retryable error
      if (onWaiting) {
        onWaiting(false, null);
      }
      return response;
    } catch (error) {
      lastError = error;

      // Check if it's a cold start / network error
      if (isColdStartError(error) && attempt < maxRetries) {
        const delay = initialDelay * Math.pow(2, attempt);
        if (onRetry) {
          onRetry(attempt + 1, delay);
        }
        if (onWaiting) {
          onWaiting(
            true,
            `Server is starting up... Please wait (attempt ${attempt + 1}/${
              maxRetries + 1
            })`
          );
        }
        await new Promise((resolve) => setTimeout(resolve, delay));
        continue;
      }

      // Non-retryable error or max retries reached
      if (onWaiting) {
        onWaiting(false, null);
      }
      throw error;
    }
  }

  // Should not reach here, but just in case
  throw lastError || new Error("Max retries exceeded");
};

/**
 * Wrapper for API calls that need retry logic with status updates
 * @param {function} apiCall - Async function that makes the API call
 * @param {object} config - Configuration
 * @param {function} config.setWaitingMessage - State setter for waiting message
 * @param {function} config.setIsWaiting - State setter for waiting state
 * @returns {Promise<any>}
 */
export const withRetryFeedback = async (apiCall, config = {}) => {
  const { setWaitingMessage = null, setIsWaiting = null } = config;

  try {
    if (setIsWaiting) setIsWaiting(true);
    if (setWaitingMessage) setWaitingMessage("Processing your request...");

    const result = await apiCall();

    if (setIsWaiting) setIsWaiting(false);
    if (setWaitingMessage) setWaitingMessage(null);

    return result;
  } catch (error) {
    if (setIsWaiting) setIsWaiting(false);
    if (setWaitingMessage) setWaitingMessage(null);
    throw error;
  }
};

export default fetchWithRetry;
